import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import type { ReportDetail, ReportOperation } from '@tallyframe/core';
import { api } from './client';
import { queryKeys } from './queries';

export function useReportDetail(workspaceId: string, reportId: string) {
  return useQuery({
    queryKey: queryKeys.report(workspaceId, reportId),
    queryFn: ({ signal }) => api.getReport(reportId, signal)
  });
}

/** Save and reload for the edit engine; each result is written into the report's cache entry. */
export function useReportPersistence(workspaceId: string): {
  save(reportId: string, revision: number, operations: ReportOperation[]): Promise<ReportDetail>;
  load(reportId: string): Promise<ReportDetail>;
} {
  const client = useQueryClient();
  return {
    save: async (reportId, revision, operations) => {
      const detail = await api.updateReport(reportId, revision, operations);
      client.setQueryData(queryKeys.report(workspaceId, reportId), detail);
      // Exact: the list shows titles; the detail was just written and must not refetch over in-flight edits.
      // Not awaited: a mounted list's refetch must not hold up every autosave.
      void client.invalidateQueries({ queryKey: queryKeys.reports(workspaceId), exact: true });
      return detail;
    },
    load: async (reportId) => {
      const detail = await api.getReport(reportId);
      client.setQueryData(queryKeys.report(workspaceId, reportId), detail);
      return detail;
    }
  };
}

export function useReportData(workspaceId: string, reportId: string, snapshotId?: string) {
  return useQuery({
    queryKey: [...queryKeys.reportData(workspaceId, reportId), snapshotId ?? null],
    queryFn: ({ signal }) => api.getReportData(reportId, snapshotId, signal)
  });
}

/** Newest first; each next page is the entries before the last one already loaded. */
export function useReportActivity(workspaceId: string, reportId: string) {
  return useInfiniteQuery({
    queryKey: queryKeys.reportActivity(workspaceId, reportId),
    initialPageParam: undefined as number | undefined,
    queryFn: ({ pageParam }) => api.listReportActivity(reportId, pageParam),
    getNextPageParam: (last) => last.entries[last.entries.length - 1]?.id
  });
}

/** Returns a function that loads one retained revision, cached per revision. */
export function useReportRevisionLoader(workspaceId: string, reportId: string) {
  const client = useQueryClient();
  return (revision: number) =>
    client.fetchQuery({
      queryKey: queryKeys.reportRevision(workspaceId, reportId, revision),
      queryFn: () => api.getReportRevision(reportId, revision)
    });
}

// Mutations fire their invalidations without returning them: a returned promise makes mutateAsync wait
// until every mounted matching query (the always-mounted reports list among them) has refetched.
function invalidateReport(client: QueryClient, workspaceId: string, reportId: string): Promise<void> {
  // The report key is a prefix-child of the list key; naming it is deliberate, so the intent survives a key change.
  return Promise.all([
    client.invalidateQueries({ queryKey: queryKeys.report(workspaceId, reportId) }),
    client.invalidateQueries({ queryKey: queryKeys.reports(workspaceId) })
  ]).then(() => undefined);
}

export function useRestoreRevision(workspaceId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (v: { reportId: string; revision: number; expectedRevision: number }) =>
      api.restoreReportRevision(v.reportId, v.revision, v.expectedRevision),
    onSuccess: (_data, v) => {
      void invalidateReport(client, workspaceId, v.reportId);
    }
  });
}

export function useRefreshReport(workspaceId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (v: { reportId: string; expectedActiveSnapshotId: string | null; snapshotId?: string }) =>
      api.refreshReport(v.reportId, v.expectedActiveSnapshotId, v.snapshotId),
    onSuccess: (_data, v) => {
      void invalidateReport(client, workspaceId, v.reportId);
    }
  });
}

export function usePublishReport(workspaceId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (v: { reportId: string; expectedRevision?: number }) =>
      api.publishReport(v.reportId, v.expectedRevision),
    onSuccess: (_data, v) => {
      void invalidateReport(client, workspaceId, v.reportId);
      // Publishing ticks "Share it" in onboarding; unpublishing never un-ticks it.
      void client.invalidateQueries({ queryKey: queryKeys.onboarding(workspaceId) });
    }
  });
}

export function useRepublishReport(workspaceId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (v: { reportId: string; expectedRevision?: number }) =>
      api.republishReport(v.reportId, v.expectedRevision),
    onSuccess: (_data, v) => {
      void invalidateReport(client, workspaceId, v.reportId);
    }
  });
}

export function useSetPublicationAutoUpdate(workspaceId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (v: { reportId: string; autoUpdate: boolean }) => api.setPublicationAutoUpdate(v.reportId, v.autoUpdate),
    onSuccess: (_data, v) => {
      void invalidateReport(client, workspaceId, v.reportId);
    }
  });
}

export function useUnpublishReport(workspaceId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (v: { reportId: string }) => api.unpublishReport(v.reportId),
    onSuccess: (_data, v) => {
      void invalidateReport(client, workspaceId, v.reportId);
    }
  });
}

export function useExportPdf() {
  return useMutation({ mutationFn: (v: { reportId: string }) => api.exportPdf(v.reportId) });
}

export function useExportCsv() {
  return useMutation({
    mutationFn: (v: { reportId: string; scope: Parameters<typeof api.exportCsv>[1] }) =>
      api.exportCsv(v.reportId, v.scope)
  });
}

export function usePreviewCsv() {
  return useMutation({ mutationFn: (file: File) => api.previewCsv(file) });
}

export function useCreateReportFromCsv(workspaceId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (v: { file: File; title: string }) => api.createReportFromCsv(v.file, v.title),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.reports(workspaceId) });
    }
  });
}

export function useCreateDataset() {
  return useMutation({ mutationFn: (input: Parameters<typeof api.createDataset>[0]) => api.createDataset(input) });
}

export function useSubmitSnapshot() {
  return useMutation({
    mutationFn: (v: { datasetId: string; input: Parameters<typeof api.submitSnapshot>[1] }) =>
      api.submitSnapshot(v.datasetId, v.input)
  });
}

export function useSubmitSnapshotCsv() {
  return useMutation({
    mutationFn: (v: { datasetId: string; file: File }) => api.submitSnapshotCsv(v.datasetId, v.file)
  });
}

export function useCreateReport(workspaceId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof api.createReport>[0]) => api.createReport(input),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.reports(workspaceId) });
    }
  });
}
